import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import CustomInput from "../../common/customInput/customInput";
import CustomButton from "../../common/customButton/customButton";
import CustomCheckbox from "../../common/customCheckbox/customCheckbox";
import { getProperty } from "../../utils/apiResponseHelper";
import UniformLoader from "../../common/uniformLoader/uniformLoader";
import {
  trackFreeDownloadSignupComplete,
  trackFreeDownloadSignupStep,
} from "../../utils/analytics";
import "./freeDownloadsRegister.css";

const FreeDownloadsRegister = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [parentName, setParentName] = useState('');
  const [childYearGroup, setChildYearGroup] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [mentorAccount, setMentorAccount] = useState(false);
  const [fdlVerified, setFDLVerified] = useState(false);
  const [fdlNotVerified, setFDLNotVerified] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [ipAddress, setIpAddress] = useState('');
  const [registering, setRegistering] = useState(false);

  const heroFormRef = useRef(null);

  // Prefix comes directly from .env file (REACT_APP_BASE_URL)
  // No modifications or normalization - use exactly as specified in .env
  const prefix = process.env.REACT_APP_BASE_URL || "https://ksol.co.uk";

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailChecked(false);
    setEmailExists(false);
    setEmailError('');
    setMentorAccount(false);
    setFDLVerified(false);
    setFDLNotVerified(false);
    setAccountExists(false);
  };

  const handleMarketingConsentChange = (e) => {
    setMarketingConsent(e.target.checked);
  };

  const getIpAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return '';
    }
  };

  useEffect(() => {
    const fetchIp = async () => {
      const ip = await getIpAddress();
      setIpAddress(ip);
    };
    fetchIp();
  }, []);

  const checkEmailExists = async () => {
    if (!email || !email.trim()) {
      setEmailError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setCheckingEmail(true);
    setEmailError('');

    try {
      const response = await fetch(
        `${prefix}/api40/Registration/CheckEmailStatus?Email=${encodeURIComponent(email.trim())}`,
        {
          headers: {
            'X-API-Key': '8dnd6%3258%&k+_FsaTK25SCBNE58*t9'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Try to parse as JSON first, fallback to text
      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const textData = await response.text();
        // Try to parse as JSON if it looks like JSON
        try {
          data = JSON.parse(textData);
        } catch {
          // If it's a plain string response, wrap it in an object
          data = { status: textData };
        }
      }

      const trimmedEmail = email.trim();

      // Get status with fallback for case sensitivity
      const status = getProperty(data[0] || data, 'status') || data[0]?.Status || data[0]?.status || data?.status || data?.Status;

      if (status === "Email_Is_Mentor" || status === "Email_Is_Mentor") {
        setMentorAccount(true);
        sessionStorage.removeItem('pendingLoginEmail');
        sessionStorage.setItem('loginRequiresUsername', 'true');
        // Navigate to login screen - always navigate within React app
        navigate('/loginScreen');
        // Also notify parent if in iframe
        if (window.parent !== window.self) {
          try {
            window.parent.postMessage({ type: 'navigate', path: '/loginScreen' }, '*');
          } catch (e) {
            // Ignore postMessage errors, navigation already happened
          }
        }
        return; // Exit early to prevent showing form fields
      } else if (status === "FDL_Verified" || status === "FDL_Verified") {
        setFDLVerified(true);
        // Store email in localStorage for full account signup later
        localStorage.setItem('email', trimmedEmail);
        // Navigate to free downloads only page - always navigate within React app
        navigate('/freeDownloadsPapers');
        // Also notify parent if in iframe
        if (window.parent !== window.self) {
          try {
            window.parent.postMessage({ type: 'navigate', path: '/freeDownloadsPapers' }, '*');
          } catch (e) {
            // Ignore postMessage errors, navigation already happened
          }
        }
        return; // Exit early to prevent showing form fields
      } else if (status === "FDL_Not_Verified" || status === "FDL_Not_Verified") {
        trackFreeDownloadSignupStep("email_entry", 1);
        setFDLNotVerified(true);
        // Store email in sessionStorage for verification screen
        sessionStorage.setItem('pendingLoginEmail', trimmedEmail);
        sessionStorage.setItem('isFreeDownloadsRegistration', 'true');
        // Also store in localStorage for persistence (for full account signup later)
        localStorage.setItem('email', trimmedEmail);
        // Send verification email (don't await to avoid delay)
        fetch(
          `${prefix}/api40/Registration/EmailVerifcationCode?Email=${encodeURIComponent(trimmedEmail)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
            },
          }
        ).catch(verifyError => {
        });
        // Navigate immediately to verification screen - don't wait for email
        navigate('/verification');
        // Also notify parent if in iframe
        if (window.parent !== window.self) {
          try {
            window.parent.postMessage({ type: 'navigate', path: '/verification' }, '*');
          } catch (e) {
            // Ignore postMessage errors, navigation already happened
          }
        }
        return; // Exit early to prevent showing form fields
      } else {
        // Email doesn't exist or status is unknown - show registration form
        trackFreeDownloadSignupStep("email_entry", 1);
        setEmailChecked(true);
        setAccountExists(false);
        setEmailError("An error occurred while checking the email");
      }
    } catch (error) {
      setEmailError("An error occurred while checking the email");
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleParentNameChange = (e) => {
    setParentName(e.target.value);
  };

  const handleChildYearGroupChange = (e) => {
    setChildYearGroup(e.target.value);
  };

  const handleRegister = async (e) => {
    e?.preventDefault();

    if (!parentName.trim()) {
      toast.error('Please enter parent name');
      return;
    }

    if (!childYearGroup) {
      toast.error('Please select child year group');
      return;
    }

    setRegistering(true);

    // Convert year group from "Year 3", "Year 4", "Year 5" or "year5", "year4" format to just "3", "4", or "5"
    const convertYearGroup = (yearGroup) => {
      if (!yearGroup) return yearGroup;
      // If it's already a number, return it as is
      if (/^\d+$/.test(yearGroup)) {
        return yearGroup;
      }
      // Extract number from "Year 3", "Year 4", "Year 5" (with space) or "year5", "year4" (no space)
      // Try with space first (e.g., "Year 3")
      let match = yearGroup.match(/year\s*(\d+)/i);
      if (match) {
        return match[1];
      }
      // Fallback: try to extract any number from the string
      match = yearGroup.match(/(\d+)/);
      return match ? match[1] : yearGroup;
    };

    const yearGroupNumber = convertYearGroup(childYearGroup);

    try {
      const response = await fetch(`${prefix}/api40/Registration/InsertFreeDownloadSignUp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': '8dnd6%3258%&k+_FsaTK25SCBNE58*t9'
        },
        body: JSON.stringify({ email:email, name:parentName, year: yearGroupNumber, ip:ipAddress, marketingConsent: marketingConsent ? "Y" : "N" }),
      });

      if (!response.ok) {
        throw new Error('Failed to register');
      }

      const data = await response.json();
      trackFreeDownloadSignupStep("download_details", 2);
      toast.success('Registration successful!');

      try {
        const recheckResponse = await fetch(`${prefix}/api40/Registration/CheckEmailStatus?Email=${encodeURIComponent(email.trim())}`, {
          headers: {
            'X-API-Key': '8dnd6%3258%&k+_FsaTK25SCBNE58*t9'
          }
        });
        if (!recheckResponse.ok) {
          throw new Error('Failed to recheck email');
        }
        const recheckData = await recheckResponse.json();
     
        // Get status with fallback for case sensitivity
        const recheckStatus = getProperty(recheckData[0] || recheckData, 'status') || recheckData[0]?.Status || recheckData[0]?.status || recheckData?.status || recheckData?.Status;

        if (recheckStatus === "FDL_Verified" || recheckStatus === "FDL_Verified") {
          // User is already verified - go directly to free downloads page
          setFDLVerified(true);
          trackFreeDownloadSignupStep("email_verification", 3);
          trackFreeDownloadSignupComplete();
          // Store email in localStorage for full account signup later
          localStorage.setItem('email', email.trim());
          navigate('/freeDownloadsPapers');
          // Also notify parent if in iframe
          if (window.parent !== window.self) {
            try {
              window.parent.postMessage({ type: 'navigate', path: '/freeDownloadsPapers' }, '*');
            } catch (e) {
              // Ignore postMessage errors
            }
          }
          return; // Exit early to prevent further execution
        } else if (recheckStatus === "FDL_Not_Verified" || recheckStatus === "FDL_Not_Verified") {
          // User needs verification - send email and navigate to verification screen
          setFDLNotVerified(true);
          sessionStorage.setItem('pendingLoginEmail', email.trim());
          sessionStorage.setItem('isFreeDownloadsRegistration', 'true');
          // Also store in localStorage for persistence (for full account signup later)
          localStorage.setItem('email', email.trim());
          
          // Send verification email
          try {
            const verifyResponse = await fetch(
              `${prefix}/api40/Registration/EmailVerifcationCode?Email=${encodeURIComponent(email.trim())}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
                },
              }
            );
            if (!verifyResponse.ok) {
            }
          } catch (verifyError) {
          }
          
          // Navigate to verification screen
          navigate('/verification');
          // Also notify parent if in iframe
          if (window.parent !== window.self) {
            try {
              window.parent.postMessage({ type: 'navigate', path: '/verification' }, '*');
            } catch (e) {
              // Ignore postMessage errors
            }
          }
          return; // Exit early to prevent further execution
        } else {
          // Unexpected status - show error
          setAccountExists(false);
          setEmailError("An error occurred while checking the email");
        }
        
      } catch (error) {
        // If recheck fails, default to verification flow
        sessionStorage.setItem('pendingLoginEmail', email.trim());
        sessionStorage.setItem('isFreeDownloadsRegistration', 'true');
        navigate('/verification');
      }
      
    } catch (error) {
      toast.error('Failed to register');
      setRegistering(false);
    }
  };

  // Scroll to top when email is checked so user can fill in the form
  useEffect(() => {
    if (emailChecked && !emailExists) {
      // Scroll to the hero form when email is checked
      setTimeout(() => {
        scrollToForm();
      }, 100);
    }
  }, [emailChecked, emailExists]);

  // Scroll to form function
  const scrollToForm = () => {
    if (heroFormRef.current) {
      heroFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  return (
    <>
      <Helmet>
        <title>Free 11+ Practice Papers & Benchmark Tests | KSOL</title>
        <meta name="description" content="Get free 11+ practice papers and 4 × 5-minute benchmark tests (Maths, English, VR, NVR) to help your child prepare for the 11+ exam. Expert-designed by experienced tutors. No card details required." />
        <meta name="keywords" content="11+ practice papers, 11+ tests, free 11+ resources, GL exam preparation, 11+ maths, 11+ english, verbal reasoning, non-verbal reasoning, KSOL" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Free 11+ Practice Papers & Benchmark Tests | KSOL" />
        <meta property="og:description" content="Get free 11+ practice papers and benchmark tests to help your child prepare for the 11+ exam. Expert-designed by experienced tutors." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free 11+ Practice Papers & Benchmark Tests | KSOL" />
        <meta name="twitter:description" content="Get free 11+ practice papers and benchmark tests to help your child prepare for the 11+ exam." />
        {/* Note: X-Frame-Options must be set via HTTP headers on the server, not meta tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
      </Helmet>
      {/* Full-page loading overlay/lightbox */}
      {registering && (
        <UniformLoader
          title="Registering your email..."
          message="Please wait while we register your email"
          subMessage="This may take a few moments"
          fullPage={true}
        />
      )}
      <div
        className="p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col items-center justify-start relative"
        style={{ backgroundColor: "#eaf4ff" }}
      >
      <div className="flex flex-col w-full items-center xl:w-3/4">
        {/* Hero Section with Form Above the Fold */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 xl:gap-12 mb-8 sm:mb-12">
          {/* Left Column: Value Proposition */}
          <div className="flex flex-col justify-center">
            <div className="bg-white drop-shadow-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 xl:p-8">
              <h1 className="text-2xl sm:text-3xl xl:text-4xl font-bold mb-3 sm:mb-4 text-gray-900">
                Free 11+ Practice Papers & Benchmark Tests
              </h1>
              <p className="text-base sm:text-lg xl:text-xl text-gray-700 mb-4 sm:mb-6">
                Get expert-designed practice papers and timed tests to help your child prepare for the 11+ exam — completely free.
              </p>
              
              {/* Quick Benefits List */}
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-start">
                  <FontAwesomeIcon 
                    icon={faCheckCircle} 
                    className="mr-2 sm:mr-3 mt-1 flex-shrink-0" 
                    style={{ color: '#9fd066', fontSize: '20px' }}
                  />
                  <p className="text-sm sm:text-base text-gray-700">Free 11+ practice papers aligned with GL exam formats</p>
                </div>
                <div className="flex items-start">
                  <FontAwesomeIcon 
                    icon={faCheckCircle} 
                    className="mr-2 sm:mr-3 mt-1 flex-shrink-0" 
                    style={{ color: '#9fd066', fontSize: '20px' }}
                  />
                  <p className="text-sm sm:text-base text-gray-700">4 × 5-minute benchmark tests (Maths, English, VR, NVR)</p>
                </div>
                <div className="flex items-start">
                  <FontAwesomeIcon 
                    icon={faCheckCircle} 
                    className="mr-2 sm:mr-3 mt-1 flex-shrink-0" 
                    style={{ color: '#9fd066', fontSize: '20px' }}
                  />
                  <p className="text-sm sm:text-base text-gray-700">Clear performance insights in just 20 minutes</p>
                </div>
              </div>

              {/* Trust Signals */}
              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-semibold">No commitment required</span> • Takes less than 30 seconds
                </p>
                <p className="text-sm text-gray-500">
                  Used by thousands of parents preparing their children for the 11+
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Registration Form */}
          <div className="flex flex-col justify-center" ref={heroFormRef}>
            <div className="bg-white drop-shadow-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 xl:p-8 border-2 sm:border-4" style={{ borderColor: '#0c57f0' }}>
              <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold mb-2" style={{ color: '#0c57f0' }}>Get Free Access</h2>
              <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 font-medium">No card details required</p>
              
              <form onSubmit={(e) => e.preventDefault()} className="w-full">
                {!emailChecked && (
                  <div className="w-full">
                    <p className="mb-2 text-gray-700">
                      Email <span style={{ color: "red" }}>*</span>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="flex-1 email-input-wrapper">
                        <CustomInput
                          type="email"
                          inputType="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={handleEmailChange}
                          autoComplete="email"
                          errorMessage={emailError}
                        />
                      </div>
                      <div className="sm:flex-shrink-0 email-button-wrapper w-full sm:w-auto">
                        <CustomButton 
                          onClick={checkEmailExists} 
                          text={checkingEmail ? "Checking..." : "Get Access"} 
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Enter your email so we can check your access and show the right next step.
                      If you already have a KSOL account, you will be asked to log in with your username and password.
                    </p>
                  </div>
                )}

                {emailChecked && !emailExists && (
                  <div className="space-y-4">
                    <div className="w-full">
                      <p className="mb-2 text-gray-700">
                        Email <span style={{ color: "red" }}>*</span>
                      </p>
                      <div className="p-4 w-full border-2 border-gray-200 rounded-lg bg-gray-50">
                        <p className="text-gray-700 font-medium">{email}</p>
                      </div>
                    </div>
                    <div className="w-full">
                      <p className="mb-2 text-gray-700">
                        Parent Name <span style={{ color: "red" }}>*</span>
                      </p>
                      <CustomInput
                        type="text"
                        inputType="text"
                        placeholder="Enter your name"
                        value={parentName}
                        onChange={handleParentNameChange}
                        autoComplete="name"
                      />
                    </div>
                    <div className="w-full">
                      <p className="mb-2 text-gray-700">
                        Child Year Group <span style={{ color: "red" }}>*</span>
                      </p>
                      <CustomInput
                        type="year"
                        inputType="year"
                        placeholder="Select year group"
                        value={childYearGroup}
                        onChange={handleChildYearGroupChange}
                        isYearDropdown={true}
                      />
                    </div>
                    <div className="w-full">
                      
                      <CustomCheckbox
                        label="I'd like to receive updates about KSOL and our products"
                        checked={marketingConsent}
                        onChange={handleMarketingConsentChange}
                      />
                    </div>
                    <div className="w-full pt-2">
                      <CustomButton 
                        onClick={handleRegister} 
                        text={registering ? "Registering..." : "Access Free Downloads"}
                        disabled={registering}
                      />
                    </div>
                    
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default FreeDownloadsRegister;
